import type { Article } from "../insights";
import { ctaPl, ctaEn, ctaZh } from "../insight-cta";

export const headhuntingItDirectSearch: Article = {
  slug: "headhunting-it-direct-search",
  updated: "2026-04-21",
  updatedLabel: {
    pl: "Aktualizacja: 21 kwietnia 2026",
    en: "Last updated: 21 April 2026",
    zh: "最近更新：2026 年 4 月 21 日",
  },
  topic: { pl: "Direct search", en: "Direct search", zh: "猎头直招" },
  pl: {
    title: "Headhunting IT: jak działa direct search w Polsce",
    h1: "Headhunting IT w Polsce 2026: jak działa direct search",
    metaTitle: "Headhunting IT w Polsce 2026: direct search krok po kroku",
    metaDescription:
      "Jak działa headhunting IT i direct search w Polsce w 2026: kiedy się opłaca, matematyka lejka pasywnych kandydatów, koszty agencji rekrutacyjnej IT i jak napisać brief.",
    eyebrow: "Insights · Direct search",
    summary:
      "Kiedy direct search wygrywa z ogłoszeniem, jak liczy się lejek kandydatów pasywnych i ile realnie kosztuje headhunting IT w Polsce.",
    lead:
      "Rekrutacja IT z ogłoszenia i headhunting to dwa różne rzemiosła, mimo że często sprzedaje się je pod jedną nazwą „agencja rekrutacyjna IT”. Prowadzimy oba procesy z Krakowa na całą Polskę i CEE, więc poniżej pokazujemy to, co realnie dzieje się w direct search: liczby z lejka, koszt, czas i to, jak odróżnić dobry brief od takiego, który kończy się zalewem przypadkowych życiorysów.",
    note:
      "Wszystkie liczby to zakresy planistyczne z naszych procesów 2025-2026, skonfrontowane z publicznymi raportami płacowymi polskiego IT. Stawki agencyjne podajemy jako % rocznego wynagrodzenia brutto kandydata.",
    readTime: "8 min czytania",
    takeaways: [
      "Direct search opłaca się głównie przy rolach senior/lead, unikalnym zestawie technologii lub gdy ogłoszenie już „wypaliło” rynek - poniżej opisujemy dokładnie kiedy.",
      "Spersonalizowana wiadomość z widełkami dostaje 35-45% odpowiedzi, generyczna 12-18%.",
      "Typowy lejek to 120-180 zmapowanych profili, 25-40 rozmów, 6-10 kandydatów przedstawionych klientowi i 1 zatrudnienie.",
      "Stawki w Polsce to zwykle 15-22% rocznego wynagrodzenia brutto, z gwarancją zastąpienia 60-90 dni.",
      "Nasz Fit-to-Win Sprint dowozi krótką listę gotową do decyzji w 14 dni od rozmowy wprowadzającej.",
      "Brak mapy rynku, brak widełek w pierwszym kontakcie i wyłączność bez planu to trzy najczęstsze sygnały ostrzegawcze przy wyborze partnera.",
    ],
    sections: [
      {
        id: "kiedy",
        eyebrow: "01 / Kiedy",
        title: "Kiedy ogłoszenie wystarczy, a kiedy potrzebny jest direct search",
        blocks: [
          {
            kind: "table",
            caption: "Ogłoszenie vs direct search - który kanał wygrywa",
            head: ["Sytuacja", "Działa ogłoszenie", "Działa direct search"],
            rows: [
              ["Rola junior/mid, popularny stack", "Tak - duża pula aplikujących", "Nieopłacalne, zbyt drogie"],
              ["Senior/Lead, wąska specjalizacja", "Rzadko - mało trafnych aplikacji", "Tak - trzeba wyjść do kandydatów"],
              ["Ogłoszenie wisi 4+ tygodnie bez efektu", "Nie - rynek już to widział", "Tak - dotarcie do pasywnych"],
              ["Rola poufna, zastępstwo osoby w firmie", "Nie - ryzyko wycieku", "Tak - dyskretny proces"],
              ["Budowa nowego zespołu/lokalizacji", "Częściowo - buduje markę pracodawcy", "Tak - trzeba aktywnie mapować rynek"],
              ["Pilna rola z ograniczonym budżetem agencyjnym", "Tak - tańszy kanał", "Nie zawsze się spina finansowo"],
            ],
          },
          {
            kind: "p",
            text: "Prosta zasada: im rzadszy profil i im ciszej ma przebiegać proces, tym mocniej przechyla się szala w stronę direct search. Ogłoszenie zbiera tych, którzy szukają teraz - headhunting dociera do tych, którzy nie szukają, ale są najlepsi na rynku.",
          },
        ],
      },
      {
        id: "lejek",
        eyebrow: "02 / Lejek",
        title: "Matematyka direct search: ile kontaktów daje jedno zatrudnienie",
        blocks: [
          {
            kind: "table",
            caption: "Typowy lejek direct search dla roli senior IT, Polska 2026",
            head: ["Etap", "Liczba", "Komentarz"],
            rows: [
              ["Zmapowane profile pasywne", "120 - 180", "Research po zestawie technologii, seniority i lokalizacji"],
              ["Wysłane spersonalizowane wiadomości", "120 - 180", "1 wiadomość = 1 profil, bez CV spamu"],
              ["Odpowiedzi (reply rate)", "25 - 40", "35-45% przy dobrej wiadomości z widełkami, 12-18% przy generycznej"],
              ["Rozmowy wstępne", "25 - 40", "Weryfikacja motywacji, widełek i realnego dopasowania"],
              ["Kandydaci przedstawieni klientowi", "6 - 10", "Po screeningu technicznym i kulturowym"],
              ["Zatrudnienie", "1", "Zwykle w ciągu 3-5 tygodni od rozmowy wprowadzającej do oferty"],
            ],
          },
          {
            kind: "p",
            text: "Różnica między 12-18% a 35-45% odpowiedzi to nie kwestia szczęścia, tylko researchu. Wiadomość, która wspomina konkretny projekt kandydata i podaje realne widełki, konwertuje niemal trzy razy lepiej niż szablon rozesłany do stu osób naraz.",
          },
        ],
      },
      {
        id: "wiadomosc",
        eyebrow: "03 / Wiadomość",
        title: "Pięć elementów wiadomości, na którą kandydaci odpowiadają",
        blocks: [
          {
            kind: "cards",
            items: [
              {
                title: "1. Konkretny powód kontaktu",
                meta: "Nie „mam dla Ciebie ciekawą ofertę”",
                body: "Odwołanie do realnego projektu, repo, wystąpienia konferencyjnego lub konkretnej technologii z profilu. Kandydat musi poczuć, że wiadomość napisano do niego, a nie do listy.",
              },
              {
                title: "2. Jawne widełki",
                meta: "Kwota, nie „konkurencyjne wynagrodzenie”",
                body: "Podanie widełek w pierwszym kontakcie odsiewa niedopasowanie w 30 sekund i jest dziś warunkiem odpowiedzi, nie elementem przetargu.",
              },
              {
                title: "3. Konkret techniczny roli",
                meta: "Stack, skala, problem",
                body: "Jedno zdanie o realnym wyzwaniu: skala ruchu, migracja, nowy zespół, budżet R&D. To odróżnia ofertę od ogólnego opisu stanowiska.",
              },
              {
                title: "4. Krótkie, jasne wezwanie do działania",
                meta: "15 minut, nie CV od razu",
                body: "Prośba o krótką rozmowę zamiast żądania CV na starcie obniża próg wejścia i zwiększa odpowiedzi wśród osób, które nie szukają aktywnie.",
              },
              {
                title: "5. Realny nadawca",
                meta: "Imię, nazwisko, telefon",
                body: "Wiadomość podpisana przez konkretną osobę z możliwością oddzwonienia buduje zaufanie szybciej niż jakikolwiek szablon korporacyjny.",
              },
            ],
          },
        ],
      },
      {
        id: "koszty",
        eyebrow: "04 / Koszty",
        title: "Ile kosztuje headhunting IT w Polsce - modele rozliczeń",
        blocks: [
          {
            kind: "table",
            caption: "Modele wynagrodzenia agencji rekrutacyjnej IT, Polska 2026",
            head: ["Model", "Koszt", "Kiedy pasuje", "Ryzyko"],
            rows: [
              ["Success fee", "15 - 22% rocznego wynagrodzenia brutto", "Pojedyncze, dobrze zdefiniowane role", "Płatność dopiero po zatrudnieniu, ale niższy priorytet agencji"],
              ["Retained search", "1/3 z góry, reszta po zatrudnieniu, łącznie 20-25%", "Role senior/lead, poufne, pilne", "Wyższy koszt wejścia, ale poszukiwania prowadzone na wyłączność od pierwszego dnia"],
              ["RPO / subskrypcja", "Stała miesięczna opłata za pojemność", "Kilka ról jednocześnie, stały dopływ kandydatów", "Wymaga przewidywalnego wolumenu, inaczej się nie zwraca"],
              ["Gwarancja zastąpienia", "Wliczona w powyższe modele", "Standard rynkowy 60-90 dni", "Bez gwarancji - sygnał ostrzegawczy przy wyborze partnera"],
            ],
          },
          {
            kind: "p",
            text: "Retained ma sens tam, gdzie porażka rekrutacji kosztuje więcej niż zaliczka - czyli przy rolach, których brak blokuje zespół lub projekt. Success fee sprawdza się przy rolach, gdzie mamy komfort czasowy i szerszą pulę kandydatów.",
          },
        ],
      },
      {
        id: "brief",
        eyebrow: "05 / Brief",
        title: "Osiem informacji, których potrzebujemy, żeby ruszyć ze sprintem",
        blocks: [
          {
            kind: "list",
            items: [
              "Realne widełki wynagrodzenia (UoP i/lub B2B), zaakceptowane wewnętrznie, nie „do ustalenia”.",
              "Konkretny stack i to, co jest must-have, a co nice-to-have - bez listy 20 technologii jako wymogów.",
              "Model pracy: zdalnie, hybrydowo, ile dni w biurze i z jakiej lokalizacji.",
              "Powód otwarcia roli: nowy zespół, zastępstwo, wzrost - to zmienia sposób prowadzenia rozmów.",
              "Struktura procesu decyzyjnego: kto rozmawia, w ilu etapach, kto podejmuje ostateczną decyzję.",
              "Maksymalny czas między etapami, jaki firma jest w stanie dotrzymać - kalendarz, nie chęci.",
              "Trzy nazwy konkurencyjnych firm lub zespołów, z których realnie chcemy pozyskać kandydatów.",
              "To, co firma oferuje poza pensją: projekty, autonomia, ścieżka rozwoju - materiał do wiadomości otwierającej.",
            ],
          },
          {
            kind: "p",
            text: "Brief bez tych ośmiu punktów kończy się zwykle jednym z dwóch scenariuw: CV spamem bez researchu albo procesem, który utyka na etapie decyzyjnym mimo dobrych kandydatów. Uczciwy partner poprosi o te dane, zanim wyśle pierwszą wiadomość.",
          },
        ],
      },
    ],
    faqTitle: "Najczęstsze pytania",
    faq: [
      {
        q: "Czym różni się headhunting od zwykłej rekrutacji z ogłoszenia?",
        a: "Ogłoszenie czeka na aplikacje osób aktywnie szukających pracy. Headhunting to aktywne dotarcie do konkretnych, często niepracujących na rynku kandydatów - wymaga researchu, spersonalizowanej wiadomości i zwykle dłuższego cyklu rozmów.",
      },
      {
        q: "Ile trwa direct search dla roli senior IT w Polsce?",
        a: "W naszym Fit-to-Win Sprint decyzyjna krótką listą jest gotowa w 14 dni od rozmowy wprowadzającej. Pełny proces do zaakceptowanej oferty trwa zwykle 3-5 tygodni, w zależności od kalendarza po stronie klienta.",
      },
      {
        q: "Czy warto podpisywać wyłączność z jedną agencją?",
        a: "Tak, jeśli agencja przedstawia konkretny plan researchu i mapę rynku przed podpisaniem umowy. Wyłączność bez planu to sygnał ostrzegawczy - oznacza zwykle brak realnej strategii dotarcia do kandydatów.",
      },
      {
        q: "Dlaczego generyczne wiadomości do kandydatów dostają tak mało odpowiedzi?",
        a: "Bo kandydaci pasywni dostają dziesiątki takich wiadomości tygodniowo. Bez konkretnego odniesienia do ich pracy i bez widełek wiadomość ląduje w tej samej kategorii co spam - stąd 12-18% odpowiedzi zamiast 35-45%.",
      },
      {
        q: "Ile kosztuje headhunting IT w Polsce w porównaniu do rekrutacji z ogłoszenia?",
        a: "Direct search to zwykle 15-22% rocznego wynagrodzenia brutto kandydata, więcej niż standardowe ogłoszenie, ale koszt trzeba liczyć razem z kosztem wakatu - im dłużej rola jest nieobsadzona, tym szybciej ta różnica się zwraca.",
      },
    ],
    cta: ctaPl,
    methodology:
      "Metodologia: dane o lejku i kosztach pochodzą z procesów direct search prowadzonych przez Positive Recruitment w latach 2025-2026, skonfrontowanych z publicznymi raportami płacowymi polskiego rynku IT. Traktuj te liczby jako zakresy planistyczne, nie gwarancje.",
  },
  en: {
    title: "IT headhunting: how direct search works in Poland",
    h1: "IT headhunting in Poland 2026: how direct search works",
    metaTitle: "IT Headhunting in Poland 2026: Direct Search Explained",
    metaDescription:
      "How IT headhunting and direct search work in Poland in 2026: when it beats a job ad, the passive candidate funnel maths, agency costs and how to brief a partner.",
    eyebrow: "Insights · Direct search",
    summary:
      "When direct search beats a job ad, how the passive candidate funnel actually adds up, and what tech headhunting in Poland really costs.",
    lead:
      "Job-ad recruitment and headhunting are two different crafts, even though both often get sold under the same label, IT recruitment agency. We run both out of Kraków across Poland and CEE, so below is what actually happens inside a direct search process: funnel numbers, cost, timelines, and how to tell a good brief from one that ends in CV spam.",
    note:
      "All figures are planning ranges from our 2025-2026 processes, cross-checked with public Polish IT salary reports. Agency fees are shown as a percentage of the candidate's gross annual salary.",
    readTime: "8 min read",
    takeaways: [
      "Direct search pays off mainly for senior/lead roles, unusual stacks, or once a job ad has already burned through the market - the breakdown below shows exactly when.",
      "A personalised message with a stated range gets a 35-45% reply rate, a generic one gets 12-18%.",
      "A typical funnel runs 120-180 mapped profiles, 25-40 conversations, 6-10 candidates presented and 1 hire.",
      "Standard Polish agency fees are 15-22% of gross annual salary, with a 60-90 day replacement guarantee.",
      "Our Fit-to-Win Sprint delivers a decision-ready shortlist in 14 days from brief.",
      "No market map, no ranges in the first message, and exclusivity without a plan are the three biggest red flags when picking a partner.",
    ],
    sections: [
      {
        id: "kiedy",
        eyebrow: "01 / When",
        title: "When a job ad is enough, and when you need direct search",
        blocks: [
          {
            kind: "table",
            caption: "Job ad vs direct search - which channel wins",
            head: ["Situation", "Job ad works", "Direct search works"],
            rows: [
              ["Junior/mid role, popular stack", "Yes - large applicant pool", "Not cost-effective"],
              ["Senior/lead, narrow specialisation", "Rarely - few relevant applications", "Yes - you have to reach out"],
              ["Ad has been live 4+ weeks with no result", "No - the market already saw it", "Yes - reaches passive candidates"],
              ["Confidential role, backfilling someone in place", "No - leak risk", "Yes - discreet process"],
              ["Building a new team or location", "Partly - builds employer brand", "Yes - needs active market mapping"],
              ["Urgent role, tight agency budget", "Yes - cheaper channel", "Doesn't always pencil out financially"],
            ],
          },
          {
            kind: "p",
            text: "Simple rule: the rarer the profile and the quieter the process needs to be, the more the balance tips toward direct search. A job ad collects people looking now, headhunting reaches the people who aren't looking but are the strongest on the market.",
          },
        ],
      },
      {
        id: "lejek",
        eyebrow: "02 / Funnel",
        title: "Direct search maths: how many contacts it takes to make one hire",
        blocks: [
          {
            kind: "table",
            caption: "Typical direct search funnel for a senior IT role, Poland 2026",
            head: ["Stage", "Number", "Note"],
            rows: [
              ["Mapped passive profiles", "120 - 180", "Research by stack, seniority and location"],
              ["Personalised messages sent", "120 - 180", "1 message per profile, no CV spam"],
              ["Replies (reply rate)", "25 - 40", "35-45% with a researched message and a range, 12-18% for a generic one"],
              ["Initial conversations", "25 - 40", "Checking motivation, range and real fit"],
              ["Candidates presented to client", "6 - 10", "After technical and cultural screening"],
              ["Hire", "1", "Usually within 3-5 weeks from brief to offer"],
            ],
          },
          {
            kind: "p",
            text: "The gap between 12-18% and 35-45% reply rates isn't luck, it's research. A message that references a candidate's actual project and states a real range converts nearly three times better than a template blasted to a hundred people at once.",
          },
        ],
      },
      {
        id: "wiadomosc",
        eyebrow: "03 / The message",
        title: "Five parts of a message that actually gets replies",
        blocks: [
          {
            kind: "cards",
            items: [
              {
                title: "1. A specific reason for reaching out",
                meta: "Not 'I have an interesting offer for you'",
                body: "A reference to a real project, repo, conference talk, or a specific technology from the profile. The candidate needs to feel the message was written for them, not to a list.",
              },
              {
                title: "2. A stated range",
                meta: "A number, not 'competitive salary'",
                body: "Stating the range on first contact filters mismatches within 30 seconds and is now a condition for a reply, not a negotiation lever.",
              },
              {
                title: "3. A technical detail about the role",
                meta: "Stack, scale, problem",
                body: "One sentence about a real challenge: traffic scale, a migration, a new team, an R&D budget. This is what separates an offer from a generic job description.",
              },
              {
                title: "4. A short, clear call to action",
                meta: "15 minutes, not a CV upfront",
                body: "Asking for a short call instead of demanding a CV upfront lowers the barrier and increases replies from people who aren't actively job hunting.",
              },
              {
                title: "5. A real sender",
                meta: "Name, surname, phone number",
                body: "A message signed by an actual person who can be called back builds trust faster than any corporate template.",
              },
            ],
          },
        ],
      },
      {
        id: "koszty",
        eyebrow: "04 / Cost",
        title: "What IT headhunting costs in Poland - fee models",
        blocks: [
          {
            kind: "table",
            caption: "IT recruitment agency fee models, Poland 2026",
            head: ["Model", "Cost", "When it fits", "Risk"],
            rows: [
              ["Success fee", "15 - 22% of gross annual salary", "Single, well-defined roles", "Paid only on hire, but lower agency priority"],
              ["Retained search", "1/3 upfront, rest on hire, 20-25% total", "Senior/lead, confidential, urgent roles", "Higher upfront cost, but dedicated research from day one"],
              ["RPO / subscription", "Fixed monthly fee for capacity", "Several roles at once, ongoing pipeline", "Needs predictable volume or it doesn't pay off"],
              ["Replacement guarantee", "Included in the models above", "Market standard 60-90 days", "No guarantee at all is a red flag when picking a partner"],
            ],
          },
          {
            kind: "p",
            text: "Retained makes sense wherever a failed hire costs more than the upfront fee, meaning roles that block a team or a project if left open. Success fee works for roles where you have time and a wider candidate pool.",
          },
        ],
      },
      {
        id: "brief",
        eyebrow: "05 / Brief",
        title: "Eight inputs we need to run a sprint",
        blocks: [
          {
            kind: "list",
            items: [
              "A real salary range (employment and/or B2B), internally approved, not 'to be discussed'.",
              "The actual stack and what's must-have versus nice-to-have, not a list of 20 required technologies.",
              "Working model: remote, hybrid, how many office days and from which location.",
              "The reason the role is open: new team, backfill, growth - this changes how the conversations are run.",
              "The decision process structure: who talks to candidates, how many stages, who makes the final call.",
              "The maximum time between stages the company can actually keep - a calendar commitment, not intentions.",
              "Three names of competitor companies or teams we should realistically be pulling candidates from.",
              "What the company offers beyond salary: projects, autonomy, growth path - material for the opening message.",
            ],
          },
          {
            kind: "p",
            text: "A brief missing these eight points usually ends in one of two ways: CV spam without research, or a process that stalls at the decision stage despite good candidates. A trustworthy partner will ask for this data before sending a single message.",
          },
        ],
      },
    ],
    faqTitle: "Frequently asked questions",
    faq: [
      {
        q: "How is headhunting different from regular job-ad recruitment?",
        a: "A job ad waits for applications from people actively looking. Headhunting actively reaches specific candidates, often not on the market at all, and requires research, a personalised message and usually a longer conversation cycle.",
      },
      {
        q: "How long does direct search take for a senior IT role in Poland?",
        a: "In our Fit-to-Win Sprint a decision-ready shortlist is ready within 14 days of the brief. The full process to an accepted offer usually takes 3-5 weeks, depending on the client's calendar.",
      },
      {
        q: "Is it worth signing exclusivity with one agency?",
        a: "Yes, if the agency presents a concrete research plan and market map before signing. Exclusivity without a plan is a red flag and usually means there's no real strategy for reaching candidates.",
      },
      {
        q: "Why do generic candidate messages get so few replies?",
        a: "Passive candidates get dozens of these messages a week. Without a specific reference to their work and without a stated range, the message lands in the same bucket as spam, hence 12-18% replies instead of 35-45%.",
      },
      {
        q: "How does IT headhunting cost compare to job-ad recruitment?",
        a: "Direct search usually runs 15-22% of the candidate's gross annual salary, more than a standard job ad, but the cost needs to be weighed against the cost of the open seat: the longer a role stays unfilled, the faster that difference pays for itself.",
      },
    ],
    cta: ctaEn,
    methodology:
      "Methodology: funnel and cost data comes from direct search processes run by Positive Recruitment in 2025-2026, cross-checked with public Polish IT salary reports. Treat these numbers as planning ranges, not guarantees.",
  },
  zh: {
    title: "IT猎头直招：直接寻访在波兰如何运作",
    h1: "波兰IT猎头2026：直接寻访实操指南",
    metaTitle: "波兰IT猎头2026：直接寻访全流程解析",
    metaDescription:
      "2026年波兰IT猎头与直接寻访实际如何运作：何时比发布招聘广告更有效、被动候选人漏斗的真实数据、猎头费用模式，以及如何撰写简报。",
    eyebrow: "洞察 · 猎头直招",
    summary:
      "何时直接寻访比招聘广告更有效、被动候选人漏斗数据是怎样计算的，以及波兰IT猎头的真实成本。",
    lead:
      "招聘广告式招聘和猎头直招是两种完全不同的手艺，尽管两者常被统称为“IT招聘公司”。我们从克拉科夫出发，在波兰全境及中东欧地区同时运营这两种流程，以下是直接寻访项目中真实发生的情况:漏斗数据、成本、时间线，以及如何区分一份好简报和一份最终只换来简历轰炸的简报。",
    note:
      "所有数字均为我们2025-2026年项目中的规划区间，并与波兰IT行业公开薪资报告进行了交叉核对。猎头费用以候选人税前年薪的百分比表示。",
    readTime: "8 分钟阅读",
    takeaways: [
      "直接寻访主要在高级/主管岗位、技术栈稀缺，或招聘广告已经“烧穿”市场时才划算，下文会具体说明何时适用。",
      "带有明确薪资区间的定制化信息回复率为35%-45%,泛泛而谈的信息只有12%-18%。",
      "典型漏斗为120-180个已梳理档案,25-40次对话,6-10名候选人提交给客户，最终录用1人。",
      "波兰标准猎头费用为候选人税前年薪的15%-22%,并附60-90天替换保证。",
      "我们的Fit-to-Win Sprint能在简报后14天内交付可供决策的候选人名单。",
      "没有市场地图、首次联系不给薪资区间、以及没有计划就要求独家合作，是挑选合作伙伴时最常见的三个警示信号。",
    ],
    sections: [
      {
        id: "kiedy",
        eyebrow: "01 / 适用场景",
        title: "什么时候招聘广告就够了，什么时候需要直接寻访",
        blocks: [
          {
            kind: "table",
            caption: "招聘广告 vs 直接寻访：哪种渠道更有效",
            head: ["场景", "招聘广告是否有效", "直接寻访是否有效"],
            rows: [
              ["初中级岗位，常见技术栈", "有效——申请者池较大", "不划算，成本过高"],
              ["高级/主管岗位，细分专长", "较少有效——匹配的申请很少", "有效——需要主动触达"],
              ["广告挂了4周以上仍无结果", "无效——市场已经看过了", "有效——能触达被动候选人"],
              ["保密岗位，替换在职人员", "无效——存在泄露风险", "有效——流程更隐秘"],
              ["组建新团队或新地点团队", "部分有效——有助于雇主品牌", "有效——需要主动做市场地图"],
              ["紧急岗位，猎头预算有限", "有效——渠道成本更低", "不一定能覆盖成本"],
            ],
          },
          {
            kind: "p",
            text: "简单的规律是：岗位越稀缺、流程越需要低调，天平就越倾向于直接寻访。招聘广告吸引的是正在找工作的人，而猎头直招触达的是不在找工作、但恰恰是市场上最优秀的人。",
          },
        ],
      },
      {
        id: "lejek",
        eyebrow: "02 / 漏斗",
        title: "直接寻访的数学：多少次联系才能促成一次录用",
        blocks: [
          {
            kind: "table",
            caption: "波兰2026年高级IT岗位典型直接寻访漏斗",
            head: ["阶段", "数量", "说明"],
            rows: [
              ["已梳理的被动候选人档案", "120 - 180", "按技术栈、级别和地点进行调研"],
              ["发送的定制化信息", "120 - 180", "一份信息对应一个档案，不做简历群发"],
              ["回复数（回复率）", "25 - 40", "有调研依据并附薪资区间的信息回复率为35%-45%,泛泛信息为12%-18%"],
              ["初步对话", "25 - 40", "确认动机、薪资区间和真实匹配度"],
              ["提交给客户的候选人", "6 - 10", "经过技术和文化筛选后"],
              ["最终录用", "1", "通常从简报到接受offer需3-5周"],
            ],
          },
          {
            kind: "p",
            text: "12%-18%和35%-45%回复率之间的差距不是运气，而是调研的结果。一条提到候选人真实项目、并给出具体薪资区间的信息，转化率几乎是群发模板的三倍。",
          },
        ],
      },
      {
        id: "wiadomosc",
        eyebrow: "03 / 触达信息",
        title: "能获得回复的信息包含的五个要素",
        blocks: [
          {
            kind: "cards",
            items: [
              {
                title: "1. 明确的联系原因",
                meta: "不是“我这里有个不错的机会”",
                body: "引用候选人真实的项目、代码仓库、会议演讲或档案中具体的技术。候选人需要感觉到这条信息是写给他本人的，而不是群发列表。",
              },
              {
                title: "2. 明确的薪资区间",
                meta: "具体数字，而非“有竞争力的薪资”",
                body: "在首次联系时就给出薪资区间，能在30秒内筛掉不匹配的情况，如今这已是获得回复的前提条件，而不是谈判筹码。",
              },
              {
                title: "3. 岗位的技术细节",
                meta: "技术栈、规模、要解决的问题",
                body: "用一句话说明真实的挑战：流量规模、系统迁移、新团队组建、研发预算。这正是区别于泛泛职位描述的关键。",
              },
              {
                title: "4. 简短明确的行动号召",
                meta: "15分钟通话，而非一开始就要简历",
                body: "邀请进行简短通话而不是一开始就索要简历，能降低门槛，提高非主动求职者的回复率。",
              },
              {
                title: "5. 真实的发信人",
                meta: "姓名、电话都真实可查",
                body: "由一个可以回拨联系的真实的人署名发出的信息，比任何企业模板都更快建立信任。",
              },
            ],
          },
        ],
      },
      {
        id: "koszty",
        eyebrow: "04 / 成本",
        title: "波兰IT猎头的费用是多少——收费模式解析",
        blocks: [
          {
            kind: "table",
            caption: "波兰2026年IT招聘公司收费模式",
            head: ["模式", "费用", "适用场景", "风险"],
            rows: [
              ["按成功付费（Success fee）", "候选人税前年薪的15%-22%", "单一、定义清晰的岗位", "仅在录用后付费，但猎头优先级较低"],
              ["预付制猎头（Retained search）", "预付三分之一，录用后付余款，总计20%-25%", "高级/主管岗位、保密或紧急岗位", "前期成本更高，但从第一天起就有专属调研团队"],
              ["RPO / 订阅制", "按容量收取固定月费", "同时招聘多个岗位、持续的招聘管道", "需要可预测的招聘量，否则不划算"],
              ["替换保证", "已包含在上述模式中", "市场标准为60-90天", "没有任何替换保证是挑选合作伙伴时的警示信号"],
            ],
          },
          {
            kind: "p",
            text: "预付制猎头适合那些一旦招聘失败、损失远超预付费用的场景，也就是空缺会拖累团队或项目进度的岗位。而按成功付费更适合有时间余量、候选人池也更宽的岗位。",
          },
        ],
      },
      {
        id: "brief",
        eyebrow: "05 / 简报",
        title: "启动一次搜寻冲刺，我们需要的八项输入信息",
        blocks: [
          {
            kind: "list",
            items: [
              "真实的薪资区间（雇佣制和/或B2B），已在内部获得批准，而非“待定”。",
              "真实的技术栈，明确哪些是必须项、哪些是加分项，而不是罗列20项技术要求。",
              "工作模式：远程、混合办公、每周到岗天数以及具体地点。",
              "岗位开放的真实原因：新团队组建、替换离职人员，还是业务增长——这会影响对话的展开方式。",
              "决策流程的结构：谁与候选人对话、共有几轮、最终由谁拍板。",
              "各环节之间公司实际能承诺的最长时间——这是日程安排的承诺，而不是意愿。",
              "三家我们应该真正从中挖掘候选人的竞争对手公司或团队名称。",
              "除薪资外公司能提供的内容：项目、自主权、成长路径——作为开场信息的素材。",
            ],
          },
          {
            kind: "p",
            text: "缺少这八项内容的简报，通常会走向两种结局之一：没有调研支撑的简历轰炸，或者即使有不错的候选人，流程也会卡在决策环节。值得信赖的合作伙伴会在发出第一条信息之前先索要这些信息。",
          },
        ],
      },
    ],
    faqTitle: "常见问题",
    faq: [
      {
        q: "猎头直招和普通的招聘广告式招聘有什么区别？",
        a: "招聘广告是被动等待主动求职者投递简历。猎头直招则是主动触达特定的候选人，他们往往根本不在求职市场上，需要调研、定制化信息，以及通常更长的沟通周期。",
      },
      {
        q: "在波兰，针对高级IT岗位的直接寻访需要多长时间？",
        a: "在我们的Fit-to-Win Sprint中，简报后14天内即可交付可供决策的候选人名单。从简报到接受offer的完整流程通常需要3-5周，具体取决于客户的日程安排。",
      },
      {
        q: "值得与一家猎头公司签独家合作吗？",
        a: "如果对方在签约前就能提供具体的调研计划和市场地图，那是值得的。没有计划就要求独家合作是一个警示信号，通常意味着对方并没有真正触达候选人的策略。",
      },
      {
        q: "为什么泛泛而谈的候选人触达信息回复率这么低？",
        a: "被动候选人每周都会收到几十条这样的信息。如果信息中没有对其具体工作的引用，也没有给出薪资区间，这类信息就会被归入垃圾信息的范畴，因此回复率只有12%-18%,而不是35%-45%。",
      },
      {
        q: "波兰IT猎头的费用与招聘广告式招聘相比如何？",
        a: "直接寻访的费用通常为候选人税前年薪的15%-22%,高于普通招聘广告的成本，但这笔费用需要与岗位空缺本身的成本对比来看：岗位空缺时间越长，这笔差额就越快被弥补回来。",
      },
    ],
    cta: ctaZh,
    methodology:
      "方法说明：漏斗和成本数据来自Positive Recruitment在2025-2026年执行的直接寻访项目，并与波兰IT行业公开薪资报告进行了交叉核对。请将这些数字视为规划区间，而非承诺保证。",
  },
};
